import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Descriptions,
  Tag,
  Badge,
  Avatar,
  List,
  Input,
  Select,
  Space,
  Divider,
  Statistic,
  Progress,
  Alert,
  Tooltip,
  Empty
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DollarOutlined,
  StarOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { organizationService } from '../services/organizationService';
import { userService } from '../services/userService';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const OrganizationDetail = ({ organizationId, onBack }) => {
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: 'all',
    status: 'all'
  });

  // Fetch organization details
  const fetchOrganization = async () => {
    setLoading(true);
    try {
      const response = await organizationService.getOrganization(organizationId);
      if (response.success) {
        setOrganization(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for this organization
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      // Get all users and filter on frontend to avoid backend filtering issues
      const response = await userService.getUsers({
        hierarchical: 'true'
      });
      if (response.success) {
        // Flatten hierarchical structure - include both parents and children
        const flatUsers = [];
        const hierarchicalUsers = response.data.users || [];
        
        console.log('Fetched all users:', hierarchicalUsers);
        console.log('Filtering for organization:', organization?._id);
        
        hierarchicalUsers.forEach(user => {
          // Check if user belongs to this organization
          if (user.tenantId === organization?._id) {
            // Add the parent user
            flatUsers.push({
              ...user,
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
            });
            
            // Add all children users
            if (user.children && user.children.length > 0) {
              user.children.forEach(child => {
                flatUsers.push({
                  ...child,
                  name: `${child.firstName || ''} ${child.lastName || ''}`.trim() || child.email
                });
              });
            }
          }
        });
        
        console.log('Filtered users for organization:', flatUsers);
        setUsers(flatUsers);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchOrganization();
    }
  }, [organizationId]);

  useEffect(() => {
    if (organization) {
      fetchUsers();
    }
  }, [organization]);

  // Apply filters on the frontend
  useEffect(() => {
    if (organization) {
      fetchUsers();
    }
  }, [userFilters]);

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    let matches = true;
    
    if (userFilters.search) {
      const searchLower = userFilters.search.toLowerCase();
      matches = matches && (
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    }
    
    if (userFilters.role !== 'all') {
      matches = matches && user.role === userFilters.role;
    }
    
    if (userFilters.status !== 'all') {
      const isActive = user.isActive || user.status === 'active';
      matches = matches && (
        (userFilters.status === 'active' && isActive) ||
        (userFilters.status === 'inactive' && !isActive)
      );
    }
    
    return matches;
  });

  // Get plan details
  const getPlanDetails = (plan) => {
    const planDetails = {
      free: {
        name: 'Free Plan',
        price: '$0',
        period: 'Forever',
        color: '#8c8c8c',
        icon: <SafetyCertificateOutlined />,
        features: [
          'Up to 5 users',
          'Basic lead management',
          'Email support',
          '1GB storage'
        ],
        limits: {
          users: 5,
          leads: 100,
          deals: 50,
          storage: '1GB'
        }
      },
      basic: {
        name: 'Basic Plan',
        price: '$29',
        period: 'per month',
        color: '#1890ff',
        icon: <StarOutlined />,
        features: [
          'Up to 25 users',
          'Advanced lead management',
          'Deal pipeline',
          'Email & chat support',
          '10GB storage',
          'Basic analytics'
        ],
        limits: {
          users: 25,
          leads: 1000,
          deals: 500,
          storage: '10GB'
        }
      },
      premium: {
        name: 'Premium Plan',
        price: '$79',
        period: 'per month',
        color: '#faad14',
        icon: <CrownOutlined />,
        features: [
          'Up to 100 users',
          'Complete CRM suite',
          'Advanced analytics',
          'Custom workflows',
          'Priority support',
          '100GB storage',
          'API access',
          'Custom integrations'
        ],
        limits: {
          users: 100,
          leads: 10000,
          deals: 5000,
          storage: '100GB'
        }
      },
      enterprise: {
        name: 'Enterprise Plan',
        price: '$199',
        period: 'per month',
        color: '#722ed1',
        icon: <CrownOutlined />,
        features: [
          'Unlimited users',
          'Complete CRM suite',
          'Advanced analytics & reporting',
          'Custom workflows & automation',
          'Dedicated support',
          'Unlimited storage',
          'Full API access',
          'Custom integrations',
          'White-label options',
          'Advanced security'
        ],
        limits: {
          users: 'Unlimited',
          leads: 'Unlimited',
          deals: 'Unlimited',
          storage: 'Unlimited'
        }
      }
    };
    
    return planDetails[plan] || planDetails.free;
  };

  // Get user role color
  const getRoleColor = (role) => {
    const colors = {
      tenant_admin: 'red',
      crm_user: 'blue',
      admin: 'red',
      user: 'blue'
    };
    return colors[role] || 'default';
  };

  // Calculate usage statistics
  const calculateUsage = () => {
    if (!organization || !users.length) return null;
    
    const planDetails = getPlanDetails(organization.plan);
    const activeUsers = users.filter(u => u.isActive || u.status === 'active').length;
    const totalUsers = users.length;
    
    const maxUsers = planDetails.limits.users === 'Unlimited' ? 1000 : planDetails.limits.users;
    const userUsagePercent = planDetails.limits.users === 'Unlimited' ? 
      (totalUsers / 100) * 100 : (totalUsers / maxUsers) * 100;
    
    return {
      users: {
        current: totalUsers,
        active: activeUsers,
        max: planDetails.limits.users,
        percentage: Math.min(userUsagePercent, 100)
      },
      leads: {
        current: organization.stats?.totalLeads || 0,
        max: planDetails.limits.leads,
        percentage: planDetails.limits.leads === 'Unlimited' ? 0 : 
          Math.min(((organization.stats?.totalLeads || 0) / planDetails.limits.leads) * 100, 100)
      },
      deals: {
        current: organization.stats?.totalDeals || 0,
        max: planDetails.limits.deals,
        percentage: planDetails.limits.deals === 'Unlimited' ? 0 : 
          Math.min(((organization.stats?.totalDeals || 0) / planDetails.limits.deals) * 100, 100)
      }
    };
  };

  if (loading || !organization) {
    return <div>Loading...</div>;
  }

  const planDetails = getPlanDetails(organization.plan);
  const usage = calculateUsage();
  const tenantAdmins = filteredUsers.filter(u => u.role === 'tenant_admin' || u.role === 'admin');
  const crmUsers = filteredUsers.filter(u => u.role === 'crm_user' || u.role === 'user');

  return (
    <div>
      {/* Back Button */}
      <div style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={onBack}
          size="large"
        >
          Back to Organizations
        </Button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          {organization.name}
        </Title>
        <Text type="secondary">Organization Details & Management</Text>
      </div>

      <Row gutter={24}>
        {/* Column 1: Organization Details */}
        <Col xs={24} lg={8}>
          <Card title="Organization Information" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Avatar 
                  size={80} 
                  icon={<BankOutlined />} 
                  style={{ backgroundColor: planDetails.color, marginBottom: 16 }}
                />
                <Title level={3} style={{ margin: 0 }}>
                  {organization.name}
                </Title>
                <Text type="secondary">{organization.subdomain}</Text>
              </div>

              <Descriptions column={1} size="small">
                <Descriptions.Item label="Status">
                  <Badge
                    status={organization.isActive ? 'success' : 'error'}
                    text={organization.isActive ? 'Active' : 'Inactive'}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Plan">
                  <Tag color={planDetails.color} icon={planDetails.icon}>
                    {planDetails.name}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  <Space>
                    <CalendarOutlined />
                    {new Date(organization.createdAt).toLocaleDateString()}
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <div>
                <Title level={5}>Contact Information</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <MailOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    <Text>{organization.contact?.email}</Text>
                  </div>
                  <div>
                    <PhoneOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                    <Text>{organization.contact?.phone || 'Not provided'}</Text>
                  </div>
                </Space>
              </div>

              <Divider />

              <div>
                <Title level={5}>Usage Statistics</Title>
                {usage && (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Users: {usage.users.current} / {usage.users.max}</Text>
                      <Progress 
                        percent={usage.users.percentage} 
                        size="small"
                        status={usage.users.percentage > 90 ? 'exception' : 'normal'}
                      />
                    </div>
                    {usage.leads.max !== 'Unlimited' && (
                      <div>
                        <Text>Leads: {usage.leads.current} / {usage.leads.max}</Text>
                        <Progress 
                          percent={usage.leads.percentage} 
                          size="small"
                          status={usage.leads.percentage > 90 ? 'exception' : 'normal'}
                        />
                      </div>
                    )}
                    {usage.deals.max !== 'Unlimited' && (
                      <div>
                        <Text>Deals: {usage.deals.current} / {usage.deals.max}</Text>
                        <Progress 
                          percent={usage.deals.percentage} 
                          size="small"
                          status={usage.deals.percentage > 90 ? 'exception' : 'normal'}
                        />
                      </div>
                    )}
                  </Space>
                )}
              </div>
            </Space>
          </Card>
        </Col>

        {/* Column 2: Users List */}
        <Col xs={24} lg={8}>
          <Card 
            title="Users Management" 
            extra={
              <Badge count={filteredUsers.length} style={{ backgroundColor: '#52c41a' }} />
            }
            style={{ marginBottom: 16 }}
          >
            {/* User Filters */}
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined />}
                value={userFilters.search}
                onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                allowClear
              />
              <Row gutter={8}>
                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Role"
                    value={userFilters.role}
                    onChange={(value) => setUserFilters(prev => ({ ...prev, role: value }))}
                  >
                    <Option value="all">All Roles</Option>
                    <Option value="tenant_admin">Tenant Admin</Option>
                    <Option value="crm_user">CRM User</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Status"
                    value={userFilters.status}
                    onChange={(value) => setUserFilters(prev => ({ ...prev, status: value }))}
                  >
                    <Option value="all">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Col>
              </Row>
            </Space>

            {/* Tenant Admins */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>
                <TeamOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                Tenant Admins ({tenantAdmins.length})
              </Title>
              <List
                size="small"
                dataSource={tenantAdmins}
                loading={usersLoading}
                locale={{ emptyText: <Empty description="No tenant admins found" /> }}
                renderItem={(user) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={<UserOutlined />} 
                          style={{ backgroundColor: '#ff4d4f' }}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{user.name}</Text>
                          <Tag color={getRoleColor(user.role)} size="small">
                            {user.role?.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{user.email}</Text>
                          <Badge
                            status={user.isActive ? 'success' : 'error'}
                            text={user.isActive ? 'Active' : 'Inactive'}
                          />
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>

            <Divider />

            {/* CRM Users */}
            <div>
              <Title level={5}>
                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                CRM Users ({crmUsers.length})
              </Title>
              <List
                size="small"
                dataSource={crmUsers}
                loading={usersLoading}
                locale={{ emptyText: <Empty description="No CRM users found" /> }}
                renderItem={(user) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={<UserOutlined />} 
                          style={{ backgroundColor: '#1890ff' }}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{user.name}</Text>
                          <Tag color={getRoleColor(user.role)} size="small">
                            {user.role?.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{user.email}</Text>
                          <Badge
                            status={user.isActive ? 'success' : 'error'}
                            text={user.isActive ? 'Active' : 'Inactive'}
                          />
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>

        {/* Column 3: Subscription Plan */}
        <Col xs={24} lg={8}>
          <Card 
            title="Subscription Plan" 
            extra={
              <Tag color={planDetails.color} icon={planDetails.icon}>
                {planDetails.name}
              </Tag>
            }
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Plan Header */}
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '48px', color: planDetails.color }}>
                  {planDetails.icon}
                </div>
                <Title level={3} style={{ margin: '16px 0 8px 0', color: planDetails.color }}>
                  {planDetails.name}
                </Title>
                <div>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold', color: planDetails.color }}>
                    {planDetails.price}
                  </Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {planDetails.period}
                  </Text>
                </div>
              </div>

              <Divider />

              {/* Plan Features */}
              <div>
                <Title level={5}>Features Included</Title>
                <List
                  size="small"
                  dataSource={planDetails.features}
                  renderItem={(feature) => (
                    <List.Item>
                      <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text>{feature}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>

              <Divider />

              {/* Plan Limits */}
              <div>
                <Title level={5}>Plan Limits</Title>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Max Users">
                    <Text strong>{planDetails.limits.users}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Max Leads">
                    <Text strong>{planDetails.limits.leads}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Max Deals">
                    <Text strong>{planDetails.limits.deals}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Storage">
                    <Text strong>{planDetails.limits.storage}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </div>

              {/* Usage Alert */}
              {usage && usage.users.percentage > 80 && (
                <Alert
                  message="Usage Warning"
                  description={`This organization is using ${Math.round(usage.users.percentage)}% of their user limit. Consider upgrading the plan.`}
                  type="warning"
                  showIcon
                />
              )}

              {/* Upgrade Suggestion */}
              {organization.plan !== 'enterprise' && (
                <div style={{ marginTop: 16 }}>
                  <Button type="primary" block icon={<CrownOutlined />}>
                    Suggest Plan Upgrade
                  </Button>
                </div>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrganizationDetail;
